import { useEffect } from 'preact/hooks';
import { createPortal } from 'preact/compat';
import { usePrice } from '../../utils/price';
import type { CarProfile } from '../../cars/CarProfile';
import { carPath } from '../../cars/registry';
import { navigate } from '../App';
import { DEMO_LIMIT_KMH, demoSeconds, demoLimitKmh, demoLeftSeconds } from '../../utils/access';
import { useT } from '../../i18n';
import { useBuy } from '../useBuy';

/** Tapped car: the CARD itself turns to glass and splits — DEMO | PURCHASE. Tap anywhere outside to leave. */
export function CarChoice({ profile, anchor, onClose }: { profile: CarProfile; anchor?: DOMRect | null; onClose: () => void }) {
  const inApp = /ROARDRIVE-App/.test(navigator.userAgent);
  const t = useT();
  const left = demoLeftSeconds(profile.id);
  const buy = useBuy(profile.id, () => { onClose(); navigate(`/garage/${carPath(profile)}`); });
  const price = usePrice();
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const r = anchor;
  const style = r ? { top: `${r.top}px`, left: `${r.left}px`, width: `${r.width}px`, height: `${r.height}px` } : undefined;
  const stacked = r ? r.height > r.width * 0.9 : true;
  return createPortal(
    <div class="splitwrap" role="dialog" aria-label={`${profile.manufacturer} ${profile.name}: demo or purchase`}>
      <div class="split__outside" onClick={onClose} />
      <div class={`split split--card ${stacked ? 'split--stacked' : ''} ${r ? '' : 'split--full'}`} style={style}>
        {/* ALWAYS open: the simulator is free with no clock, so the used-up road demo can no longer lock
            somebody out of the app they just downloaded (Marc, as CEO) */}
        <button class="split__half split__half--demo" onClick={() => navigate(`/garage/${carPath(profile)}`)}>
          <span class="split__word">{t('demoWord')}</span>
          <span class="split__sub">{t('demoSub')}</span>
          <span class="split__fine">{left > 0 ? (demoLimitKmh() == null ? t(inApp ? 'demoFineLeftOpen' : 'demoFineLeftOpenWeb', { min: Math.ceil(left / 60) }) : t(inApp ? 'demoFineLeft' : 'demoFineLeftWeb', { min: Math.ceil(left / 60), cap: DEMO_LIMIT_KMH })) : t(inApp ? 'demoFineUsed' : 'demoFineUsedWeb', { min: demoSeconds() / 60 })}</span>
        </button>
        <div class="split__line" aria-hidden="true" />
        <button class="split__half split__half--buy" disabled={buy.busy || (buy.canBuy && buy.onSale === false)} onClick={() => (buy.canBuy ? void buy.buy() : navigate(`/unlock/${carPath(profile)}`))}>
          <span class="split__word">{buy.busy ? buy.busyLabel : t('purchase')}</span>
          <span class="split__sub">{buy.canBuy && buy.onSale === false ? t('notInAppYet') : buy.price || price}</span>
          <span class="split__fine">{buy.note || t('purchaseFine')}</span>
        </button>
      </div>
    </div>,
    document.body,
  );
}
